import { useState, useEffect, useCallback } from "react";
import { Building2, Plus, Trash2, MapPin, Calendar, Ruler, Pencil, X } from "lucide-react";
import axios, { API } from "@/utils/api";
import { useT } from "@/contexts/LanguageContext";

const EMPTY_FORM = { building_name: "", address: "", city: "", country: "", building_type: "", total_area_sqft: "", year_built: "" };

const AssetManagerView = ({ clientId, onAssetChange }) => {
  const t = useT();
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const fetchAssets = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/clients/${clientId}/assets`);
      setAssets(res.data);
    } catch (err) {
      console.error("Error fetching assets:", err);
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => { fetchAssets(); }, [fetchAssets]);

  const handleSave = async () => {
    if (!form.building_name.trim()) { setError("Building name is required"); return; }
    setError("");
    setSaving(true);
    try {
      if (editingId) {
        await axios.put(`${API}/assets/${editingId}`, form);
      } else {
        await axios.post(`${API}/clients/${clientId}/assets`, form);
      }
      await fetchAssets();
      setShowForm(false);
      setEditingId(null);
      setForm(EMPTY_FORM);
      if (onAssetChange) onAssetChange();
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (asset) => {
    setForm({
      building_name: asset.building_name || "",
      address: asset.address || "",
      city: asset.city || "",
      country: asset.country || "",
      building_type: asset.building_type || "",
      total_area_sqft: asset.total_area_sqft || "",
      year_built: asset.year_built || "",
    });
    setEditingId(asset.asset_uuid);
    setShowForm(true);
    setError("");
  };

  const handleDelete = async (assetUuid) => {
    if (assets.length <= 1) { alert("Cannot delete the last asset."); return; }
    if (!window.confirm("Delete this asset and all its associated energy data?")) return;
    try {
      await axios.delete(`${API}/assets/${assetUuid}`);
      await fetchAssets();
      if (onAssetChange) onAssetChange();
    } catch (err) {
      alert(err.response?.data?.detail || err.message);
    }
  };

  const openNewForm = () => {
    setForm(EMPTY_FORM);
    setEditingId(null);
    setShowForm(true);
    setError("");
  };

  if (loading) return <div className="loading-container"><div className="spinner"></div></div>;

  return (
    <div className="view-container" data-testid="asset-manager-view">
      <div className="view-header">
        <div>
          <h2>{t("assets_title")}</h2>
          <p className="text-muted">{t("assets_desc")}</p>
        </div>
        <button className="btn btn-primary btn-sm" onClick={openNewForm} data-testid="add-asset-btn">
          <Plus size={16} /> {t("add_asset")}
        </button>
      </div>

      {showForm && (
        <div className="asset-form-card" data-testid="asset-form">
          <div className="asset-form-header">
            <h3>{editingId ? t("edit_asset") : t("new_asset")}</h3>
            <button className="auth-modal-close" onClick={() => { setShowForm(false); setEditingId(null); }} style={{ position: "static" }}>
              <X size={18} />
            </button>
          </div>
          {error && <div className="alert alert-danger" style={{ marginBottom: "12px", fontSize: "0.85rem" }}>{error}</div>}
          <div className="asset-form-grid">
            <div className="form-group">
              <label className="form-label">Building Name *</label>
              <input className="form-input" value={form.building_name} onChange={(e) => setForm({ ...form, building_name: e.target.value })} placeholder="Main Office Building" data-testid="asset-name-input" />
            </div>
            <div className="form-group">
              <label className="form-label">Building Type</label>
              <input className="form-input" value={form.building_type} onChange={(e) => setForm({ ...form, building_type: e.target.value })} placeholder="Office, Warehouse, Retail..." data-testid="asset-type-input" />
            </div>
            <div className="form-group">
              <label className="form-label">Address</label>
              <input className="form-input" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="123 Main St" data-testid="asset-address-input" />
            </div>
            <div className="form-group">
              <label className="form-label">City</label>
              <input className="form-input" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder="Montreal" data-testid="asset-city-input" />
            </div>
            <div className="form-group">
              <label className="form-label">Country</label>
              <input className="form-input" value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} placeholder="Canada" data-testid="asset-country-input" />
            </div>
            <div className="form-group">
              <label className="form-label">Total Area (sq ft)</label>
              <input className="form-input" type="number" value={form.total_area_sqft} onChange={(e) => setForm({ ...form, total_area_sqft: e.target.value })} placeholder="50000" data-testid="asset-area-input" />
            </div>
            <div className="form-group">
              <label className="form-label">Year Built</label>
              <input className="form-input" type="number" value={form.year_built} onChange={(e) => setForm({ ...form, year_built: e.target.value })} placeholder="1995" data-testid="asset-year-input" />
            </div>
          </div>
          <div style={{ display: "flex", gap: "8px", marginTop: "12px" }}>
            <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving} data-testid="asset-save-btn">
              {saving ? "Saving..." : editingId ? "Update Asset" : "Create Asset"}
            </button>
            <button className="btn btn-secondary btn-sm" onClick={() => { setShowForm(false); setEditingId(null); }}>Cancel</button>
          </div>
        </div>
      )}

      <div className="assets-grid">
        {assets.map((asset) => (
          <div className="asset-card" key={asset.asset_uuid} data-testid={`asset-card-${asset.asset_uuid}`}>
            <div className="asset-card-header">
              <Building2 size={20} />
              <h3>{asset.building_name}</h3>
              <div className="asset-card-actions">
                <button className="btn-icon" onClick={() => handleEdit(asset)} title="Edit" data-testid={`asset-edit-${asset.asset_uuid}`}><Pencil size={14} /></button>
                {assets.length > 1 && (
                  <button className="btn-icon btn-icon-danger" onClick={() => handleDelete(asset.asset_uuid)} title="Delete" data-testid={`asset-delete-${asset.asset_uuid}`}><Trash2 size={14} /></button>
                )}
              </div>
            </div>
            <div className="asset-card-details">
              {asset.building_type && <span className="asset-detail-tag">{asset.building_type}</span>}
              {asset.address && <div className="asset-detail-row"><MapPin size={13} /> {asset.address}{asset.city ? `, ${asset.city}` : ""}{asset.country ? `, ${asset.country}` : ""}</div>}
              {asset.total_area_sqft && <div className="asset-detail-row"><Ruler size={13} /> {Number(asset.total_area_sqft).toLocaleString()} sq ft</div>}
              {asset.year_built && <div className="asset-detail-row"><Calendar size={13} /> Built {asset.year_built}</div>}
            </div>
            <div className="asset-card-id">
              <span className="text-muted" style={{ fontSize: "0.7rem" }}>UUID: {asset.asset_uuid}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AssetManagerView;
